import {
    DisplayColumnDef,
    ExpandedState,
    VisibilityState,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable
} from "@tanstack/react-table";
import React from "react";
import { useHistory } from "react-router-dom";
import Loader from "../Loader";
import { BulkActions, TableHeadCell } from "./components";
import TableRow from "./components/TableRow";
import { expandColumn, selectColumn } from "./defaultColumns";
import { useTableRowSelection, useTableSorting } from "./hooks";
import * as S from "./styled";
import { IItem, ITableProps, TRow } from "./types";

const DEFAULT_EMPTY_STATE = "Uh-oh! Couldn't find anything. Try again later.";

const Table = <TItem extends IItem, TColumnName extends string>({
    columnDefinitions,
    renderExtraContent,
    renderBulkActions,
    items,
    getRowLink,
    rowHeight,
    footer,
    sorting: sortingProp,
    onSortingChange: onSortingChangeProp,
    loading = false,
    disableColumnAutoSizing = false,
    enableRowSelection = false,
    selectedItems: selectedItemsProp,
    onRowSelectionChange: onRowSelectionChangeProp,
    emptyState = DEFAULT_EMPTY_STATE,
    columnOrder,
    visibleColumns,
    builtIn = true,
    getRowProps,
    variant = "default",
    tableHead = true,
    tableHeight,
    tableMaxHeight,
    ...props
}: ITableProps<TItem, TColumnName>) => {
    const history = useHistory();
    const [expanded, setExpanded] = React.useState<ExpandedState>({});
    const { selectedItems, rowSelection, onRowSelectionChange } = useTableRowSelection<TItem>(
        items,
        selectedItemsProp,
        onRowSelectionChangeProp
    );
    const { enableSorting, sorting, onSortingChange } = useTableSorting(sortingProp, onSortingChangeProp);

    const columns = React.useMemo<DisplayColumnDef<TItem, any>[]>(() => {
        return [
            ...(enableRowSelection ? [selectColumn] : []),
            ...columnDefinitions,
            ...(renderExtraContent ? [expandColumn] : [])
        ] as DisplayColumnDef<TItem, any>[];
    }, [columnDefinitions, renderExtraContent]);

    const columnVisibility = React.useMemo<VisibilityState | undefined>(() => {
        if (visibleColumns) {
            if (Array.isArray(visibleColumns)) {
                return columns.reduce((acc, col) => {
                    const columnId = col.id! as TColumnName;
                    acc[columnId] = visibleColumns.includes(columnId);
                    return acc;
                }, {} as Record<TColumnName, boolean>);
            }
            return columns.reduce((acc, col) => {
                const columnId = col.id! as TColumnName;
                acc[columnId] = visibleColumns[columnId] ?? true;
                return acc;
            }, {} as Record<TColumnName, boolean>);
        }
        return undefined;
    }, [visibleColumns]);

    const table = useReactTable<TItem>({
        data: items,
        columns,
        defaultColumn: enableSorting
            ? {
                  enableSorting: false,
                  accessorKey: "_"
              }
            : undefined,
        state: {
            expanded,
            sorting,
            columnOrder,
            rowSelection,
            columnVisibility
        },
        enableSorting,
        onSortingChange,
        enableRowSelection,
        manualSorting: true,
        onExpandedChange: setExpanded,
        onRowSelectionChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: enableSorting ? getSortedRowModel() : undefined
    });

    const emptyStateJsx = typeof emptyState === "string" ? <S.EmptyState>{emptyState}</S.EmptyState> : emptyState;

    const isEmpty = items.length === 0;

    const handleClick = getRowLink
        ? (row: TRow<TItem>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
              const rowLink = getRowLink?.(row.original);
              if (e.ctrlKey || e.metaKey) {
                  window.open(rowLink, "_blank", "noopener noreferrer");
              } else {
                  history.push(rowLink);
              }
          }
        : undefined;

    return (
        <S.TableContainer builtIn={builtIn} {...props} $variant={variant}>
            <S.TableInnerContainer $height={tableHeight} $maxHeight={tableMaxHeight}>
                <S.Table cellSpacing="0">
                    {tableHead && (
                        <S.TableHead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <S.TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map(header => (
                                        <TableHeadCell
                                            key={header.id}
                                            header={header}
                                            disableColumnAutoSizing={disableColumnAutoSizing}
                                        />
                                    ))}
                                </S.TableRow>
                            ))}
                        </S.TableHead>
                    )}
                    {!loading && (
                        <S.TableBody>
                            {table.getRowModel().rows.map(row => (
                                <TableRow
                                    key={row.id}
                                    row={row}
                                    onClick={handleClick ? e => handleClick(row, e) : undefined}
                                    renderExtraContent={renderExtraContent}
                                    rowHeight={rowHeight}
                                    getRowProps={getRowProps}
                                />
                            ))}
                        </S.TableBody>
                    )}
                </S.Table>
                {loading && (
                    <S.Loader $rowHeight={rowHeight}>
                        <Loader size={1.5} />
                    </S.Loader>
                )}
                {isEmpty && !loading && emptyStateJsx}
            </S.TableInnerContainer>
            {!isEmpty && !loading && footer}
            {selectedItems.length > 0 && renderBulkActions && (
                <BulkActions.Portal.Root>{renderBulkActions(selectedItems)}</BulkActions.Portal.Root>
            )}
        </S.TableContainer>
    );
};

Table.BulkActions = BulkActions;

export default Table;
